/**
 * Google Analytics 4 helpers (gtag.js script — no Firebase SDK).
 *
 * WARNING — Do not pass PII/PHI into trackEvent() or trackPageView().
 * Never send: email, phone, name, DOB, gender, zipcode, member ID,
 * insurance details, RD name, appointment date/time, Calendly event_uri,
 * invitee_uri, access tokens, JWTs, or query parameters.
 */

export const GA_MEASUREMENT_ID =
  process.env.REACT_APP_GA_MEASUREMENT_ID?.trim() || "";

const ALLOWED_PARAM_KEYS = new Set([
  "product",
  "platform",
  "step_name",
  "step_number",
  "from_step",
  "payment_type",
  "method",
  "result",
  "provider_id",
  "flow_name",
  "status",
  "error_type",
  "booking_type",
]);

const ALLOWED_PRODUCTS = new Set(["nutrition"]);
const ALLOWED_PLATFORMS = new Set(["web"]);

const ALLOWED_STEP_NAMES = new Set([
  "account",
  "profile",
  "insurance",
  "payment",
  "card_on_file",
  "provider",
  "booking",
  "completed",
]);

const ALLOWED_PAYMENT_TYPES = new Set(["insurance", "self_pay"]);

const ALLOWED_INSURANCE_METHODS = new Set(["auto", "manual"]);
const ALLOWED_INSURANCE_RESULTS = new Set(["verified", "fallback", "failed"]);

const ALLOWED_FLOW_NAMES = new Set(["new_registration"]);
const ALLOWED_STATUS_VALUES = new Set(["success", "failed"]);

const ALLOWED_ERROR_TYPES = new Set([
  "api_failed",
  "validation_failed",
  "unauthorized",
  "unknown",
]);

const ALLOWED_BOOKING_TYPES = new Set(["virtual_initial"]);

const PROVIDER_ID_PATTERN = /^[a-zA-Z0-9_-]+$/;

let analyticsInitialized = false;

export const isAnalyticsEnabled = () =>
  Boolean(GA_MEASUREMENT_ID && typeof window !== "undefined");

const normalizePathSegment = (pathname = "") => {
  const withoutQuery = String(pathname).split("?")[0].split("#")[0];
  const normalized = withoutQuery.replace(/\/+$/, "") || "/";

  return normalized
    .replace(
      /\/no-preference\/dietitians\/[^/]+/,
      "/no-preference/dietitians/:rdId"
    )
    .replace(/\/dietitians\/[^/]+/, "/dietitians/:rdId");
};

/**
 * Returns a clean pathname without query/hash and with dynamic IDs redacted.
 */
export const sanitizePathname = (pathname) => normalizePathSegment(pathname);

const sanitizeProviderId = (value) => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (
      trimmed.length > 0 &&
      trimmed.length <= 64 &&
      PROVIDER_ID_PATTERN.test(trimmed)
    ) {
      return trimmed;
    }
  }

  return null;
};

const sanitizeEventParams = (params = {}) => {
  if (!params || typeof params !== "object") {
    return {};
  }

  const sanitized = {};

  Object.entries(params).forEach(([key, value]) => {
    if (!ALLOWED_PARAM_KEYS.has(key)) {
      return;
    }

    if (key === "product" && ALLOWED_PRODUCTS.has(value)) {
      sanitized.product = value;
      return;
    }

    if (key === "platform" && ALLOWED_PLATFORMS.has(value)) {
      sanitized.platform = value;
      return;
    }

    if (key === "step_name" && ALLOWED_STEP_NAMES.has(value)) {
      sanitized.step_name = value;
      return;
    }

    if (key === "from_step" && ALLOWED_STEP_NAMES.has(value)) {
      sanitized.from_step = value;
      return;
    }

    if (
      key === "step_number" &&
      typeof value === "number" &&
      Number.isInteger(value) &&
      value >= 1 &&
      value <= 20
    ) {
      sanitized.step_number = value;
      return;
    }

    if (key === "payment_type" && ALLOWED_PAYMENT_TYPES.has(value)) {
      sanitized.payment_type = value;
      return;
    }

    if (key === "method" && ALLOWED_INSURANCE_METHODS.has(value)) {
      sanitized.method = value;
      return;
    }

    if (key === "result" && ALLOWED_INSURANCE_RESULTS.has(value)) {
      sanitized.result = value;
      return;
    }

    if (key === "provider_id") {
      const providerId = sanitizeProviderId(value);
      if (providerId != null) {
        sanitized.provider_id = providerId;
      }
      return;
    }

    if (key === "flow_name" && ALLOWED_FLOW_NAMES.has(value)) {
      sanitized.flow_name = value;
      return;
    }

    if (key === "status" && ALLOWED_STATUS_VALUES.has(value)) {
      sanitized.status = value;
      return;
    }

    if (key === "error_type" && ALLOWED_ERROR_TYPES.has(value)) {
      sanitized.error_type = value;
      return;
    }

    if (key === "booking_type" && ALLOWED_BOOKING_TYPES.has(value)) {
      sanitized.booking_type = value;
    }
  });

  return sanitized;
};

const ensureGtagStub = () => {
  if (typeof window.gtag === "function") {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };
  window.gtag("js", new Date());
};

const loadGtagScript = () =>
  new Promise((resolve) => {
    ensureGtagStub();

    const existing = document.querySelector(
      `script[src*="googletagmanager.com/gtag/js"][src*="${GA_MEASUREMENT_ID}"]`
    );
    if (existing) {
      resolve();
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.onload = () => resolve();
    script.onerror = () => resolve();
    document.head.appendChild(script);
  });

/**
 * Loads GA4 via gtag.js when REACT_APP_GA_MEASUREMENT_ID is set.
 * Configures send_page_view: false so page views are sent manually.
 */
export const initAnalytics = async () => {
  if (!isAnalyticsEnabled() || analyticsInitialized) {
    return;
  }

  ensureGtagStub();
  await loadGtagScript();

  if (typeof window.gtag !== "function") {
    return;
  }

  window.gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false,
  });

  analyticsInitialized = true;
};

/**
 * Sends a manual page view for the given pathname (no query params).
 *
 * WARNING — Pass only sanitized pathnames. Do not include PII/PHI.
 */
export const trackPageView = (pathname) => {
  if (!isAnalyticsEnabled() || typeof window.gtag !== "function") {
    return;
  }

  const pagePath = sanitizePathname(pathname);

  window.gtag("event", "page_view", {
    page_path: pagePath,
    page_location: `${window.location.origin}${pagePath}`,
  });
};

/**
 * Sends a GA4 custom event with an allow-listed parameter set.
 *
 * WARNING — Never pass PII/PHI in params. Only use the documented
 * registration analytics helpers when tracking the signup flow.
 */
export const trackEvent = (eventName, params = {}) => {
  if (
    !isAnalyticsEnabled() ||
    typeof window.gtag !== "function" ||
    !eventName
  ) {
    return;
  }

  const sanitizedParams = sanitizeEventParams(params);
  window.gtag("event", eventName, sanitizedParams);
};
